import { useEffect, useState } from 'react';
import { assetAPI, loanAPI, creditCardAPI } from '../api';
import { Plus, Wallet, Building2, TrendingUp, CreditCard as CreditCardIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Assets() {
  const [tab, setTab] = useState('assets');
  const [assets, setAssets] = useState([]);
  const [loans, setLoans] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assetsRes, loansRes, cardsRes] = await Promise.all([
        assetAPI.getAll(),
        loanAPI.getAll(),
        creditCardAPI.getAll(),
      ]);
      setAssets(assetsRes.data);
      setLoans(loansRes.data);
      setCreditCards(cardsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const handleDeleteAsset = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await assetAPI.delete(id);
      toast.success('Asset deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleDeleteLoan = async (id) => {
    if (!window.confirm('Delete this loan?')) return;
    try {
      await loanAPI.delete(id);
      toast.success('Loan deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete loan');
    }
  };

  const handleDeleteCard = async (id) => {
    if (!window.confirm('Delete this credit card?')) return;
    try {
      await creditCardAPI.delete(id);
      toast.success('Credit card deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete credit card');
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto'></div>
          <p className='mt-4 text-stone-600'>Loading assets...</p>
        </div>
      </div>
    );
  }

  const totalAssets = assets.reduce((sum, a) => sum + a.current_balance, 0);
  const totalLiabilities = loans
    .filter((l) => l.role === 'I_owe')
    .reduce((sum, l) => sum + l.outstanding_amount, 0);

  return (
    <div className='min-h-screen bg-stone-50 p-6 md:p-12'>
      <div className='max-w-6xl mx-auto'>
        <div className='flex justify-between items-start mb-8'>
          <div>
            <h1 className='text-4xl md:text-5xl font-bold font-heading text-green-900 mb-2'>
              Assets & Loans
            </h1>
            <p className='text-stone-500 text-lg'>Manage your wealth and liabilities</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid='add-item-btn'
            className='bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95'
          >
            <Plus className='inline-block w-5 h-5 mr-2' />
            Add {tab === 'assets' ? 'Asset' : tab === 'loans' ? 'Loan' : 'Card'}
          </button>
        </div>

        {/* Summary Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
          <div className='bg-white rounded-2xl p-6 border border-stone-200 shadow-sm' data-testid='total-assets-card'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='text-xs font-bold text-stone-500 uppercase tracking-widest'>Total Assets</h3>
              <Wallet className='w-6 h-6 text-green-900' strokeWidth={1.5} />
            </div>
            <div className='text-4xl font-bold text-green-900 tabular-nums'>
              {totalAssets.toFixed(2)}
              <span className='text-xl ml-2 text-stone-500'>SAR</span>
            </div>
            <p className='text-sm text-stone-500 mt-2'>{assets.length} accounts</p>
          </div>
          <div className='bg-white rounded-2xl p-6 border border-stone-200 shadow-sm' data-testid='total-liabilities-card'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='text-xs font-bold text-stone-500 uppercase tracking-widest'>Total Liabilities</h3>
              <TrendingUp className='w-6 h-6 text-red-700' strokeWidth={1.5} />
            </div>
            <div className='text-4xl font-bold text-red-700 tabular-nums'>
              {totalLiabilities.toFixed(2)}
              <span className='text-xl ml-2 text-stone-500'>SAR</span>
            </div>
            <p className='text-sm text-stone-500 mt-2'>{loans.filter((l) => l.role === 'I_owe').length} active loans</p>
          </div>
        </div>

        {/* Tabs */}
        <div className='flex gap-4 mb-6'>
          <button
            onClick={() => setTab('assets')}
            data-testid='tab-assets'
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              tab === 'assets' ? 'bg-green-900 text-white shadow-lg' : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
          >
            Assets ({assets.length})
          </button>
          <button
            onClick={() => setTab('loans')}
            data-testid='tab-loans'
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              tab === 'loans' ? 'bg-green-900 text-white shadow-lg' : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
          >
            Loans ({loans.length})
          </button>
          <button
            onClick={() => setTab('cards')}
            data-testid='tab-cards'
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              tab === 'cards' ? 'bg-green-900 text-white shadow-lg' : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
          >
            Cards ({creditCards.length})
          </button>
        </div>

        {/* Content */}
        {tab === 'assets' && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6' data-testid='assets-list'>
            {assets.length === 0 ? (
              <div className='col-span-2 bg-white rounded-2xl p-12 text-center border border-stone-200'>
                <Wallet className='w-16 h-16 text-stone-300 mx-auto mb-4' />
                <p className='text-stone-500'>No assets added yet</p>
              </div>
            ) : (
              assets.map((asset) => (
                <div
                  key={asset.id}
                  className='bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-md transition-all'
                  data-testid={`asset-${asset.id}`}
                >
                  <div className='flex justify-between items-start mb-4'>
                    <div>
                      <span className='px-3 py-1 bg-stone-100 text-stone-700 text-xs font-bold rounded-full'>
                        {asset.type}
                      </span>
                      <h3 className='text-xl font-bold text-green-900 mt-2'>{asset.name}</h3>
                    </div>
                    <button
                      onClick={() => handleDeleteAsset(asset.id)}
                      data-testid={`delete-asset-${asset.id}`}
                      className='p-2 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all'
                    >
                      <Trash2 className='w-5 h-5' />
                    </button>
                  </div>
                  <div className='mb-3'>
                    <p className='text-sm text-stone-500 mb-1'>Current Balance</p>
                    <p className='text-3xl font-bold text-green-900 tabular-nums'>
                      {asset.current_balance.toFixed(2)} <span className='text-lg text-stone-500'>{asset.currency}</span>
                    </p>
                  </div>
                  {asset.notes && <p className='text-sm text-stone-600 mt-2'>{asset.notes}</p>}
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'loans' && (
          <div className='space-y-4' data-testid='loans-list'>
            {loans.length === 0 ? (
              <div className='bg-white rounded-2xl p-12 text-center border border-stone-200'>
                <Building2 className='w-16 h-16 text-stone-300 mx-auto mb-4' />
                <p className='text-stone-500'>No loans added yet</p>
              </div>
            ) : (
              loans.map((loan) => (
                <div
                  key={loan.id}
                  className='bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-md transition-all'
                  data-testid={`loan-${loan.id}`}
                >
                  <div className='flex justify-between items-start'>
                    <div className='flex-1'>
                      <div className='flex items-center gap-3 mb-2'>
                        <span
                          className={`px-3 py-1 text-xs font-bold rounded-full ${
                            loan.role === 'I_owe'
                              ? 'bg-red-100 text-red-900'
                              : 'bg-green-100 text-green-900'
                          }`}
                        >
                          {loan.role === 'I_owe' ? 'I Owe' : 'Others Owe Me'}
                        </span>
                        <span className='px-3 py-1 bg-stone-100 text-stone-700 text-xs font-bold rounded-full'>
                          {loan.status}
                        </span>
                      </div>
                      <h3 className='text-xl font-bold text-green-900 mb-2'>{loan.lender_or_borrower_name}</h3>
                      <div className='grid grid-cols-2 gap-4 text-sm'>
                        <div>
                          <p className='text-stone-500'>Outstanding</p>
                          <p className='font-bold text-red-700 tabular-nums'>{loan.outstanding_amount.toFixed(2)} SAR</p>
                        </div>
                        <div>
                          <p className='text-stone-500'>Interest Rate</p>
                          <p className='font-bold tabular-nums'>{loan.interest_rate}%</p>
                        </div>
                        {loan.emi_amount && (
                          <>
                            <div>
                              <p className='text-stone-500'>EMI Amount</p>
                              <p className='font-bold tabular-nums'>{loan.emi_amount.toFixed(2)} SAR</p>
                            </div>
                            <div>
                              <p className='text-stone-500'>EMI Due Day</p>
                              <p className='font-bold tabular-nums'>{loan.emi_due_day}</p>
                            </div>
                          </>
                        )}
                      </div>
                      {loan.purpose && <p className='text-sm text-stone-600 mt-3'>{loan.purpose}</p>}
                    </div>
                    <button
                      onClick={() => handleDeleteLoan(loan.id)}
                      data-testid={`delete-loan-${loan.id}`}
                      className='p-2 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all'
                    >
                      <Trash2 className='w-5 h-5' />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'cards' && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6' data-testid='cards-list'>
            {creditCards.length === 0 ? (
              <div className='col-span-2 bg-white rounded-2xl p-12 text-center border border-stone-200'>
                <CreditCardIcon className='w-16 h-16 text-stone-300 mx-auto mb-4' />
                <p className='text-stone-500'>No credit cards added yet</p>
              </div>
            ) : (
              creditCards.map((card) => (
                <div
                  key={card.id}
                  className='bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-md transition-all'
                  data-testid={`card-${card.id}`}
                >
                  <div className='flex justify-between items-start mb-4'>
                    <div>
                      <h3 className='text-xl font-bold text-green-900'>{card.card_name}</h3>
                      <p className="text-sm text-stone-500 mt-1">{card.bank_name} **** {card.last4}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteCard(card.id)}
                      data-testid={`delete-card-${card.id}`}
                      className='p-2 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all'
                    >
                      <Trash2 className='w-5 h-5' />
                    </button>
                  </div>
                  <div className='grid grid-cols-2 gap-4 text-sm'>
                    <div>
                      <p className='text-stone-500'>Credit Limit</p>
                      <p className='font-bold text-green-900 tabular-nums'>{card.credit_limit.toFixed(2)} SAR</p>
                    </div>
                    <div>
                      <p className='text-stone-500'>Billing Cycle</p>
                      <p className='font-bold tabular-nums'>Day {card.billing_cycle_start_day}</p>
                    </div>
                    <div>
                      <p className='text-stone-500'>Payment Due</p>
                      <p className='font-bold tabular-nums'>Day {card.billing_due_day}</p>
                    </div>
                    <div>
                      <p className='text-stone-500'>Status</p>
                      <span
                        className={`px-2 py-1 text-xs font-bold rounded-full ${
                          card.status === 'active' ? 'bg-green-100 text-green-900' : 'bg-stone-100 text-stone-700'
                        }`}
                      >
                        {card.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddItemModal
          type={tab}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function AddItemModal({ type, onClose, onSuccess }) {
  const [formData, setFormData] = useState(
    type === 'assets'
      ? {
          type: 'bank',
          name: '',
          opening_balance: '',
          current_balance: '',
          currency: 'SAR',
        }
      : type === 'loans'
      ? {
          role: 'I_owe',
          lender_or_borrower_name: '',
          principal_amount: '',
          outstanding_amount: '',
          currency: 'SAR',
          interest_rate: '',
          start_date: new Date().toISOString().split('T')[0],
          emi_amount: '',
          emi_due_day: '',
        }
      : {
          bank_name: '',
          card_name: '',
          last4: '',
          credit_limit: '',
          billing_cycle_start_day: '',
          billing_due_day: '',
        }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (type === 'assets') {
        await assetAPI.create({
          ...formData,
          opening_balance: parseFloat(formData.opening_balance),
          current_balance: parseFloat(formData.current_balance),
        });
        toast.success('Asset added successfully!');
      } else if (type === 'loans') {
        await loanAPI.create({
          ...formData,
          principal_amount: parseFloat(formData.principal_amount),
          outstanding_amount: parseFloat(formData.outstanding_amount),
          interest_rate: parseFloat(formData.interest_rate),
          emi_amount: formData.emi_amount ? parseFloat(formData.emi_amount) : null,
          emi_due_day: formData.emi_due_day ? parseInt(formData.emi_due_day) : null,
        });
        toast.success('Loan added successfully!');
      } else {
        await creditCardAPI.create({
          ...formData,
          credit_limit: parseFloat(formData.credit_limit),
          billing_cycle_start_day: parseInt(formData.billing_cycle_start_day),
          billing_due_day: parseInt(formData.billing_due_day),
        });
        toast.success('Credit card added successfully!');
      }
      onSuccess();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    }
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4' onClick={onClose}>
      <div
        className='bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto'
        onClick={(e) => e.stopPropagation()}
        data-testid='add-item-modal'
      >
        <h2 className='text-2xl font-bold font-heading text-green-900 mb-6'>
          Add {type === 'assets' ? 'Asset' : type === 'loans' ? 'Loan' : 'Credit Card'}
        </h2>
        <form onSubmit={handleSubmit} className='space-y-4'>
          {type === 'assets' && (
            <>
              <div>
                <label className='block text-sm font-bold text-stone-700 mb-2'>Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                  data-testid='input-type'
                  className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                >
                  <option value='cash'>Cash</option>
                  <option value='bank'>Bank</option>
                  <option value='investment'>Investment</option>
                  <option value='property'>Property</option>
                  <option value='vehicle'>Vehicle</option>
                  <option value='other'>Other</option>
                </select>
              </div>
              <div>
                <label className='block text-sm font-bold text-stone-700 mb-2'>Name</label>
                <input
                  type='text'
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid='input-name'
                  className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                />
              </div>
              <div>
                <label className='block text-sm font-bold text-stone-700 mb-2'>Opening Balance (SAR)</label>
                <input
                  type='number'
                  step='0.01'
                  value={formData.opening_balance}
                  onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value, current_balance: e.target.value })}
                  required
                  data-testid='input-balance'
                  className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                />
              </div>
            </>
          )}
          {type === 'loans' && (
            <>
              <div>
                <label className='block text-sm font-bold text-stone-700 mb-2'>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  data-testid='input-role'
                  className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                >
                  <option value='I_owe'>I Owe</option>
                  <option value='others_owe_me'>Others Owe Me</option>
                </select>
              </div>
              <div>
                <label className='block text-sm font-bold text-stone-700 mb-2'>Lender/Borrower Name</label>
                <input
                  type='text'
                  value={formData.lender_or_borrower_name}
                  onChange={(e) => setFormData({ ...formData, lender_or_borrower_name: e.target.value })}
                  required
                  data-testid='input-lender-name'
                  className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                />
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-bold text-stone-700 mb-2'>Principal Amount</label>
                  <input
                    type='number'
                    step='0.01'
                    value={formData.principal_amount}
                    onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value, outstanding_amount: e.target.value })}
                    required
                    data-testid='input-principal'
                    className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                  />
                </div>
                <div>
                  <label className='block text-sm font-bold text-stone-700 mb-2'>Interest Rate (%)</label>
                  <input
                    type='number'
                    step='0.01'
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    required
                    data-testid='input-interest'
                    className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                  />
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-bold text-stone-700 mb-2'>EMI Amount (optional)</label>
                  <input
                    type='number'
                    step='0.01'
                    value={formData.emi_amount}
                    onChange={(e) => setFormData({ ...formData, emi_amount: e.target.value })}
                    data-testid='input-emi-amount'
                    className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                  />
                </div>
                <div>
                  <label className='block text-sm font-bold text-stone-700 mb-2'>EMI Due Day</label>
                  <input
                    type='number'
                    min='1'
                    max='31'
                    value={formData.emi_due_day}
                    onChange={(e) => setFormData({ ...formData, emi_due_day: e.target.value })}
                    data-testid='input-emi-day'
                    className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                  />
                </div>
              </div>
            </>
          )}
          {type === 'cards' && (
            <>
              <div>
                <label className='block text-sm font-bold text-stone-700 mb-2'>Bank Name</label>
                <input
                  type='text'
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  required
                  data-testid='input-bank-name'
                  className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                />
              </div>
              <div>
                <label className='block text-sm font-bold text-stone-700 mb-2'>Card Name</label>
                <input
                  type='text'
                  value={formData.card_name}
                  onChange={(e) => setFormData({ ...formData, card_name: e.target.value })}
                  required
                  data-testid='input-card-name'
                  className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                />
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-bold text-stone-700 mb-2'>Last 4 Digits</label>
                  <input
                    type='text'
                    maxLength='4'
                    value={formData.last4}
                    onChange={(e) => setFormData({ ...formData, last4: e.target.value })}
                    required
                    data-testid='input-last4'
                    className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                  />
                </div>
                <div>
                  <label className='block text-sm font-bold text-stone-700 mb-2'>Credit Limit</label>
                  <input
                    type='number'
                    step='0.01'
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                    required
                    data-testid='input-credit-limit'
                    className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                  />
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-bold text-stone-700 mb-2'>Billing Cycle Start</label>
                  <input
                    type='number'
                    min='1'
                    max='31'
                    value={formData.billing_cycle_start_day}
                    onChange={(e) => setFormData({ ...formData, billing_cycle_start_day: e.target.value })}
                    required
                    data-testid='input-cycle-start'
                    className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                  />
                </div>
                <div>
                  <label className='block text-sm font-bold text-stone-700 mb-2'>Payment Due Day</label>
                  <input
                    type='number'
                    min='1'
                    max='31'
                    value={formData.billing_due_day}
                    onChange={(e) => setFormData({ ...formData, billing_due_day: e.target.value })}
                    required
                    data-testid='input-due-day'
                    className='w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all'
                  />
                </div>
              </div>
            </>
          )}
          <div className='flex gap-3 pt-4'>
            <button
              type='button'
              onClick={onClose}
              data-testid='modal-cancel-btn'
              className='flex-1 px-6 py-3 bg-stone-100 text-stone-900 rounded-full font-medium hover:bg-stone-200 transition-all'
            >
              Cancel
            </button>
            <button
              type='submit'
              data-testid='modal-submit-btn'
              className='flex-1 px-6 py-3 bg-green-900 text-white rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95'
            >
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
